/**
 * Whether the contracts `GET /config` names may be approved, funded and signed
 * against.
 *
 * ## Why this exists
 *
 * §1.5 is why the client hardcodes no address: the exchange and the collateral
 * token both rotate, and a client holding a stale one signs orders for a
 * contract that no longer settles them. The consequence nobody wrote down
 * is that **every address this client acts on is chosen by the server**, and
 * until this module the only thing checked about one was that it had forty hex
 * digits. A hostile or compromised `/config` could name any contract at all and
 * the client would ask the user to `approve` it, `deposit` into it and sign
 * orders naming it as `verifyingContract` — three actions that move or commit
 * real money, all authorised by the user's own wallet, none of them recoverable.
 * `docs/17`, CRITICAL, and it is two rows rather than one because the EIP-712
 * domain carries its own copy of the exchange address.
 *
 * ## What it can and cannot buy
 *
 * Stated plainly, because the difference decides how much the layers below are
 * worth.
 *
 * - **A pin is the only thing that stops a hostile server.** `VITE_UD_CHAIN_PINS`
 *   is deployment configuration — the client still ships no environment's
 *   address, the *deployment* does — and where one exists for the connected
 *   chain it is authoritative: divergence is a refusal, never a prompt.
 * - **The consistency checks stop a compromised half and every misconfiguration.**
 *   `chain.exchange_address` and `eip712.domain.verifying_contract` are two
 *   independently published fields naming one contract; a chain id that the
 *   wallet, the config and this build do not all agree on is a staging client
 *   about to sign for production. None of this stops a server that publishes a
 *   self-consistent hostile set, and saying otherwise would be worse than not
 *   checking.
 * - **Trust-on-first-use covers the deployments with no pin**, and it is in
 *   `wallet/chain-trust-store.ts` rather than here because it needs storage and
 *   this module is pure.
 *
 * ## Why it is in `domain/`
 *
 * Invariant 10: no React, no wagmi, no `viem`. It is imported by
 * `domain/order-validation.ts`, which is in the eager graph — a `wallet/`
 * import here would put the signing stack on every anonymous visitor's cold
 * load (invariant 13, §F21k). That is also why `SUPPORTED_CHAIN_IDS` is written
 * out below rather than derived from `wallet/connectors.ts`'s wagmi chains;
 * `connectors.test.ts` asserts the two lists agree, which is the direction that
 * costs nothing at runtime.
 */

/**
 * The chains this build will talk to — `wallet/connectors.ts`'s
 * `SUPPORTED_CHAINS`, as ids. Arbitrum One and Arbitrum Sepolia, because
 * docs/04 §1.5 puts staging on `421614` and production on `42161`.
 *
 * **Advisory, and deliberately not the refusal.** A local Anvil is `31337` and
 * every on-chain harness in this repository runs against one, so a hard list
 * here would refuse the only chain the contracts are ever exercised on. What
 * refuses instead is narrower and stronger: a configured deployment's own pin
 * table *is* the chain allowlist — a chain with no pin, in a build that pins
 * anything, is not one this deployment settles on — and the wallet's chain must
 * equal the venue's whatever the build. Between them there is no way to sign or
 * approve on a chain nobody chose, and no rule that a development chain has to
 * break to work.
 */
export const SUPPORTED_CHAIN_IDS: readonly number[] =
  // Plus a local Anvil in a development build, as `SUPPORTED_CHAINS` does.
  import.meta.env.DEV ? [42161, 421614, 31337] : [42161, 421614]

/**
 * What a user calls each chain in `SUPPORTED_CHAIN_IDS` — the wallet's own
 * network menu says "Arbitrum Sepolia", never `421614`. `connectors.test.ts`
 * asserts this table covers that list, for the same reason it asserts the list.
 */
const CHAIN_NAMES: Readonly<Record<number, string>> = {
  42161: 'Arbitrum One',
  421614: 'Arbitrum Sepolia',
  31337: 'Local Anvil',
}

/** A chain's name, or `chain {id}` for one this build does not talk to. */
export function chainName(chainId: number): string {
  return CHAIN_NAMES[chainId] ?? `chain ${chainId}`
}

/** Whether this build can ask a wallet to switch to `chainId` — wagmi only switches to a chain it is configured with. */
export function canSwitchTo(chainId: number): boolean {
  return SUPPORTED_CHAIN_IDS.includes(chainId)
}

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

export interface ChainTargets {
  collateral: `0x${string}`
  exchange: `0x${string}`
  chainId: number
}

/**
 * One chain's pinned contracts, as `VITE_UD_CHAIN_PINS` carries them.
 *
 * Two since `docs/06` §F93, which removed `conditional_tokens_address` from
 * `/config`: there is no outcome token for a user to approve, so there is no
 * third contract to pin. A pin or a remembered entry that still names one is
 * read for the two members below and the rest is ignored.
 */
export interface ChainPin {
  exchange: string
  collateral: string
}

export type ChainPins = Readonly<Record<string, ChainPin>>

export type ChainTrustCode =
  /** Not forty hex digits, or the zero address. */
  | 'ADDRESS_MALFORMED'
  /** A chain id this build does not talk to at all. */
  | 'CHAIN_UNSUPPORTED'
  /** The wallet is connected somewhere else. An approval granted there looks
   *  like a success and authorises nothing. */
  | 'WALLET_ON_WRONG_CHAIN'
  /** `chain.exchange_address` and `eip712.domain.verifying_contract` name two
   *  different contracts. One of them is wrong and neither can be preferred. */
  | 'DOMAIN_DISAGREES'
  /** A pin exists for this chain and the venue published something else. */
  | 'PIN_MISMATCH'
  /** No pin, and the addresses are not the ones this browser saw before. */
  | 'ADDRESSES_CHANGED'

export type ChainTrustVerdict =
  | {
      trusted: true
      /** `PINNED` is the strong case; `UNPINNED` says consistency held and
       *  nothing authoritative was available to check it against. */
      basis: 'PINNED' | 'UNPINNED'
    }
  | {
      trusted: false
      code: ChainTrustCode
      /** One sentence, rendered as-is. `docs/07` §7. */
      detail: string
      /** What the venue published and what was expected, both spelled out —
       *  an address the user cannot see is one they cannot check. */
      observed?: string
      expected?: string
    }

export interface ChainTrustInput {
  targets: ChainTargets | null
  /** `config.eip712.domain`, already renamed by `toEip712Domain`. */
  domain?: { chainId?: number; verifyingContract?: string } | null
  /** The chain the wallet is actually connected to, or `null` when unread.
   *  `null` is not a mismatch — an unread chain has told us nothing. */
  walletChainId?: number | null
  pins?: ChainPins
  /** What this browser trusted on a previous visit, or `null` for a first one.
   *  Ignored entirely when a pin exists. */
  seen?: ChainPin | null
}

const eq = (a: string | undefined, b: string | undefined): boolean =>
  typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase()

/** Forty hex digits **and not the zero address**, which is the one well-formed
 *  address that is never a contract and is what an unset field serialises to. */
export function isContractAddress(value: unknown): value is `0x${string}` {
  return (
    typeof value === 'string' &&
    /^0x[0-9a-fA-F]{40}$/.test(value) &&
    value.toLowerCase() !== ZERO_ADDRESS
  )
}

/**
 * The whole decision, in the order the failures are worth reporting.
 *
 * Shape first (a malformed address makes every later comparison meaningless),
 * then the chain, then the two published copies of the exchange, then the pin,
 * then first-use. The first failure wins: a user told three things at once
 * checks none of them.
 */
export function verifyChainTargets(input: ChainTrustInput): ChainTrustVerdict {
  const { targets } = input
  if (!targets) {
    return {
      trusted: false,
      code: 'ADDRESS_MALFORMED',
      detail: 'This venue has not published its contract addresses.',
    }
  }

  for (const [name, address] of [
    ['collateral token', targets.collateral],
    ['exchange', targets.exchange],
  ] as const) {
    if (!isContractAddress(address)) {
      return {
        trusted: false,
        code: 'ADDRESS_MALFORMED',
        detail: `The venue published an address for the ${name} that cannot be a contract.`,
        observed: String(address),
      }
    }
  }

  // The pin table is the chain allowlist. A deployment that pins anything has
  // said which chains it settles on; one that pins nothing has said nothing,
  // and refusing on a hardcoded list there would refuse every local chain.
  const pins = input.pins ?? {}
  const pinnedChains = Object.keys(pins)
  if (pinnedChains.length > 0 && !(String(targets.chainId) in pins)) {
    return {
      trusted: false,
      code: 'CHAIN_UNSUPPORTED',
      detail: `This venue settles on chain ${targets.chainId}, which this app is not configured for.`,
      observed: String(targets.chainId),
      expected: pinnedChains.join(' or '),
    }
  }

  // `null` means unread. A wallet that has not answered has not disagreed, and
  // refusing on it would block every path before the connector has attached.
  if (typeof input.walletChainId === 'number' && input.walletChainId !== targets.chainId) {
    return {
      trusted: false,
      code: 'WALLET_ON_WRONG_CHAIN',
      detail: 'Your wallet is connected to a different network than this venue settles on.',
      observed: String(input.walletChainId),
      expected: String(targets.chainId),
    }
  }

  // The exchange is published twice — once as an address and once inside every
  // order digest — and the two are compared here rather than trusted apart.
  // This is the cheapest disagreement to detect and the first one a partial
  // compromise trips: rewriting `chain.exchange_address` without rewriting the
  // domain gives a user's signature to one contract and their approval to
  // another.
  const domain = input.domain ?? null
  if (domain) {
    if (typeof domain.verifyingContract === 'string' && !eq(domain.verifyingContract, targets.exchange)) {
      return {
        trusted: false,
        code: 'DOMAIN_DISAGREES',
        detail: 'The venue named two different exchange contracts in one response.',
        observed: domain.verifyingContract,
        expected: targets.exchange,
      }
    }
    if (typeof domain.chainId === 'number' && domain.chainId !== targets.chainId) {
      return {
        trusted: false,
        code: 'DOMAIN_DISAGREES',
        detail: 'The venue named two different chains in one response.',
        observed: String(domain.chainId),
        expected: String(targets.chainId),
      }
    }
  }

  const pin = pins[String(targets.chainId)]
  if (pin) {
    const mismatch =
      (!eq(pin.exchange, targets.exchange) && ['exchange', pin.exchange, targets.exchange]) ||
      (!eq(pin.collateral, targets.collateral) && ['collateral token', pin.collateral, targets.collateral])
    if (mismatch) {
      const [what, expected, observed] = mismatch as [string, string, string]
      return {
        trusted: false,
        code: 'PIN_MISMATCH',
        detail: `The venue published a ${what} contract this app is not configured to trust.`,
        observed,
        expected,
      }
    }
    return { trusted: true, basis: 'PINNED' }
  }

  // No pin. Trust-on-first-use: a first visit is trusted and remembered, and a
  // later change has to be looked at by a person. It does not protect a first
  // load, and it is not offered as if it did — `basis: 'UNPINNED'` is what the
  // panel says out loud.
  const seen = input.seen ?? null
  if (seen) {
    const changed =
      (!eq(seen.exchange, targets.exchange) && ['exchange', seen.exchange, targets.exchange]) ||
      (!eq(seen.collateral, targets.collateral) && [
        'collateral token',
        seen.collateral,
        targets.collateral,
      ])
    if (changed) {
      const [what, expected, observed] = changed as [string, string, string]
      return {
        trusted: false,
        code: 'ADDRESSES_CHANGED',
        detail: `This venue's ${what} contract has changed since you last used it.`,
        observed,
        expected,
      }
    }
  }

  return { trusted: true, basis: 'UNPINNED' }
}

/** The pair, in the shape trust-on-first-use stores. */
export function pinOf(targets: ChainTargets): ChainPin {
  return {
    exchange: targets.exchange.toLowerCase(),
    collateral: targets.collateral.toLowerCase(),
  }
}

// ---------------------------------------------------------------------------
// The signing domain
// ---------------------------------------------------------------------------

/**
 * Whether `config.eip712.domain` may be signed against at all.
 *
 * **The domain is entirely server-chosen and was validated against nothing.**
 * `toEip712Domain` is a *lossless renamer* — its own tests pin that it invents
 * nothing and drops nothing, which is right, because defaulting a missing
 * `chainId` would make a staging client sign for production. What was missing
 * is the refusal on the other side of that: an empty `eip712` block produced
 * `{}`, and `{}` is a perfectly valid EIP-712 domain. A signature over it is
 * well-formed, recovers to the signer, and commits them to a struct with **no
 * chain and no contract in its digest** — which is a replayable order on any
 * chain that will take it. `docs/17`, CRITICAL.
 *
 * So: all four members present, and the two that also appear in
 * `config.chain` must agree with it — which is `verifyChainTargets`, called
 * here rather than restated, so the pin covers the digest's copy of the
 * exchange address as well as the one the user approves.
 */
export type SigningDomainVerdict =
  | { signable: true }
  | { signable: false; code: ChainTrustCode | 'DOMAIN_INCOMPLETE'; detail: string }

export interface SigningDomain {
  name?: string
  version?: string
  chainId?: number
  verifyingContract?: string
}

export function verifySigningDomain(
  domain: SigningDomain | null | undefined,
  input: Omit<ChainTrustInput, 'domain'>,
): SigningDomainVerdict {
  const d = domain ?? {}
  const missing = (
    [
      ['name', typeof d.name === 'string' && d.name !== ''],
      ['version', typeof d.version === 'string' && d.version !== ''],
      ['chainId', typeof d.chainId === 'number' && Number.isFinite(d.chainId)],
      ['verifyingContract', isContractAddress(d.verifyingContract)],
    ] as const
  )
    .filter(([, ok]) => !ok)
    .map(([name]) => name)

  if (missing.length > 0) {
    return {
      signable: false,
      code: 'DOMAIN_INCOMPLETE',
      // Named, because this is the one failure a developer reads rather than a
      // user: it means `/config` is not serving what openapi requires.
      detail: `This venue's signing configuration is incomplete (${missing.join(', ')}), so nothing can be signed against it.`,
    }
  }

  const verdict = verifyChainTargets({ ...input, domain: d })
  if (!verdict.trusted) return { signable: false, code: verdict.code, detail: verdict.detail }
  return { signable: true }
}
